import moment, { Duration } from 'moment-timezone'
import { format } from 'date-fns'
import { Locale } from 'date-fns/locale'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { EventCalendar } from '@/types/Calendar.ts'
import { Work, WorkType } from '@/types/work.ts'

function filterEventsByDayOfWeek(events: EventCalendar[], dayNumber: number): EventCalendar[] {
    return [...events].filter((event: EventCalendar) => moment(event.start).day() === dayNumber)
}

interface WeeklyEvents {
    monday: EventCalendar[]
    tuesday: EventCalendar[]
    wednesday: EventCalendar[]
    thursday: EventCalendar[]
    friday: EventCalendar[]
    saturday: EventCalendar[]
    sunday: EventCalendar[]
}

export function filterEventsForWeek(events: EventCalendar[]): WeeklyEvents {
    return {
        monday: filterEventsByDayOfWeek(events, 1),
        tuesday: filterEventsByDayOfWeek(events, 2),
        wednesday: filterEventsByDayOfWeek(events, 3),
        thursday: filterEventsByDayOfWeek(events, 4),
        friday: filterEventsByDayOfWeek(events, 5),
        saturday: filterEventsByDayOfWeek(events, 6),
        sunday: filterEventsByDayOfWeek(events, 0)
    }
}

export function mapEventsWithClassName<T extends EventCalendar>(
    events: T[],
    className: string
): (T & { className: string })[] {
    return events.map((event: T) => ({
        ...event,
        className
    }))
}

export function filterMoreLinkEvents(
    events: EventCalendar[],
    moreLinkDate: string,
    className?: string
): EventCalendar[] {
    const locale: Locale = getLocalFromLanguage()

    const filtered: EventCalendar[] = events.filter((event: EventCalendar): boolean => {
        return format(event.start, 'EEEE dd MMMM yyyy', { locale }) === moreLinkDate
    })

    if (className) {
        return filtered.map((event: EventCalendar) => ({
            ...event,
            className
        }))
    }

    return filtered
}

export function sortEventsByDate(events: EventCalendar[]): EventCalendar[] {
    return [...events].sort(
        (a: EventCalendar, b: EventCalendar): number =>
            moment(a.start).diff(moment(b.start)) || moment(a.end).diff(moment(b.end))
    )
}

export function getHoursAndMinutesFromWorks(works: Work[]): { hours: number; minutes: number } {
    const totalDurationInMinutes: Duration = moment.duration(
        works
            .filter((work: Work): boolean => work.type !== WorkType.holiday)
            .reduce((total: number, work: Work): number => total + work.totalDuration, 0),
        'minutes'
    )

    return {
        hours: Math.floor(totalDurationInMinutes.asHours()),
        minutes: totalDurationInMinutes.minutes()
    }
}

export function formatHoursMinutes(hours: number, minutes: number): string {
    return `${String(hours).padStart(2, '0')}H${String(minutes).padStart(2, '0')}`
}
